/**
 * AI Tutor Page - Epic 85: Student AI Tutoring Interface
 * Implements SSE-based streaming chat with Anthropic Claude Sonnet 4.5
 * Carbon Design System compliant
 */

import React, { useState, useRef, useEffect } from 'react';
import { Grid, Column, Button, TextArea, InlineLoading, InlineNotification, Dropdown, Tile } from '@carbon/react';
import { Send, HelpFilled, Restart } from '@carbon/icons-react';

interface Message {
  id: string;
  sender: 'student' | 'ai';
  text: string;
  timestamp: Date;
}

const AITutorPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [subject, setSubject] = useState('mathematics');
  const [sessionId] = useState(`session-${Date.now()}`);
  const [characterCount, setCharacterCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentStreamRef = useRef<string>('');

  const subjects = [
    { id: 'mathematics', text: 'Mathematics' },
    { id: 'science', text: 'Science' },
    { id: 'language_arts', text: 'Language Arts' },
    { id: 'social_studies', text: 'Social Studies' },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isStreaming) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      sender: 'student',
      text: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setCharacterCount(0);
    setIsStreaming(true);
    setError(null);

    // Prepare AI message placeholder
    const aiMessageId = `ai-${Date.now()}`;
    currentStreamRef.current = '';

    try {
      const response = await fetch('/api/v1/tutoring/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: inputMessage,
          subject: subject,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let buffer = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();

        if (done) {
          streamDone = true;
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            try {
              const parsed = JSON.parse(data);

              if (parsed.token) {
                // Accumulate tokens
                currentStreamRef.current += parsed.token;

                // Update UI every 5-10 tokens for batched rendering
                setMessages(prev => {
                  const existing = prev.find(m => m.id === aiMessageId);
                  if (existing) {
                    return prev.map(m => (m.id === aiMessageId ? { ...m, text: currentStreamRef.current } : m));
                  } else {
                    return [
                      ...prev,
                      {
                        id: aiMessageId,
                        sender: 'ai',
                        text: currentStreamRef.current,
                        timestamp: new Date(),
                      },
                    ];
                  }
                });
              } else if (parsed.done) {
                // Stream complete
                console.log('Stream complete', parsed.usage);
              }
            } catch (e) {
              console.error('JSON parse error:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Tutoring error:', err);
      setError('Failed to get AI response. Please try again.');
    } finally {
      setIsStreaming(false);
      currentStreamRef.current = '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 500) {
      setInputMessage(value);
      setCharacterCount(value.length);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const requestHint = () => {
    setInputMessage(inputMessage + ' Can you give me a hint?');
    setCharacterCount((inputMessage + ' Can you give me a hint?').length);
  };

  const requestDifferentExplanation = () => {
    setInputMessage(inputMessage + ' Can you explain this differently?');
    setCharacterCount((inputMessage + ' Can you explain this differently?').length);
  };

  const markAsUnderstood = async () => {
    const lastAiMessage = messages.filter(m => m.sender === 'ai').pop();
    if (lastAiMessage) {
      // TODO: Call API to increment mastery score
      setInputMessage("I understand! What's next?");
      setCharacterCount("I understand! What's next?".length);
    }
  };

  return (
    <Grid fullWidth className="ai-tutor-page">
      <Column lg={16} md={8} sm={4}>
        <div style={{ padding: '2rem' }}>
          <h1 style={{ marginBottom: '1rem' }}>AI Tutor</h1>

          {/* Academic Integrity Reminder */}
          <InlineNotification
            kind="info"
            title="Academic Integrity Reminder"
            subtitle="I'm here to help you learn, not to give you answers. Let's work through problems together!"
            lowContrast
            style={{ marginBottom: '1rem' }}
          />

          {/* Error Notification */}
          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {/* Subject Selector */}
          <div style={{ marginBottom: '1rem', maxWidth: '300px' }}>
            <Dropdown
              id="subject-dropdown"
              titleText="Subject"
              label="Select subject"
              items={subjects}
              itemToString={item => (item ? item.text : '')}
              selectedItem={subjects.find(s => s.id === subject)}
              onChange={({ selectedItem }) => {
                if (selectedItem) {
                  setSubject(selectedItem.id);
                }
              }}
            />
          </div>

          {/* Chat Messages */}
          <Tile
            style={{
              height: '500px',
              overflowY: 'auto',
              marginBottom: '1rem',
              padding: '1rem',
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#525252',
                  textAlign: 'center',
                }}
              >
                <p>Start a conversation! Ask me anything about {subjects.find(s => s.id === subject)?.text}.</p>
              </div>
            )}

            {messages.map(msg => (
              <div
                key={msg.id}
                style={{
                  marginBottom: '1rem',
                  display: 'flex',
                  justifyContent: msg.sender === 'student' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '70%',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    backgroundColor: msg.sender === 'student' ? '#0f62fe' : '#8a3ffc',
                    color: '#ffffff',
                  }}
                >
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                  <small style={{ opacity: 0.8, fontSize: '0.75rem' }}>{msg.timestamp.toLocaleTimeString()}</small>
                </div>
              </div>
            ))}

            {isStreaming && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <InlineLoading description="AI is thinking..." />
              </div>
            )}

            <div ref={messagesEndRef} />
          </Tile>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={HelpFilled}
              onClick={requestHint}
              disabled={isStreaming || !inputMessage.trim()}
            >
              Request Hint
            </Button>
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Restart}
              onClick={requestDifferentExplanation}
              disabled={isStreaming || !inputMessage.trim()}
            >
              Different Explanation
            </Button>
            <Button
              kind="ghost"
              size="sm"
              onClick={markAsUnderstood}
              disabled={isStreaming || messages.filter(m => m.sender === 'ai').length === 0}
            >
              I Understand
            </Button>
          </div>

          {/* Input Area */}
          <div style={{ position: 'relative' }}>
            <TextArea
              id="tutor-input"
              labelText="Your message"
              placeholder="Ask a question or describe what you're working on..."
              value={inputMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              rows={3}
              disabled={isStreaming}
              maxLength={500}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '0.5rem',
              }}
            >
              <span style={{ fontSize: '0.875rem', color: '#525252' }}>{characterCount}/500</span>
              <Button
                kind="primary"
                renderIcon={Send}
                onClick={sendMessage}
                disabled={isStreaming || !inputMessage.trim()}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </Column>
    </Grid>
  );
};

export default AITutorPage;
